/**
 * URL <-> filter-state codec for shareable Track Translations links.
 *
 * `trackStateToParams` writes it, `paramsToTrackState` reads it back with
 * validation. `namespace` is the signal a link is a real shared search — the
 * page can't query without one.
 */
import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import type { UrlStateCodec } from 'src/common/urlSharing';
import { dateRangeValidationCode } from 'src/common/utils/dateFormatters';

/** The filter selection a shared Track link restores. */
export interface TrackUrlState {
  namespace: string;
  /** String group id, or '' for all groups. */
  stringGroup: string;
  /** Date range, or null when there is no date filter. */
  dateRange: DateRangePickerProps.Value | null;
}

/**
 * Query-string keys. Each concept uses the same token as its path segment
 * (`ns`, `sg` — see src/common/routes.ts), so one URL never spells a concept
 * two ways.
 *
 * `sg` replaced `group` with no fallback read of the old key: the product is
 * pre-launch, so old-shape links are allowed to break (the same policy that
 * shipped the route prefixes without legacy redirects), and saved track
 * filters persist the decoded state rather than URL strings, so no stored
 * data carries the old key. A dual-read would keep two spellings of one
 * concept alive with nothing to migrate.
 */
const PARAM = {
  NAMESPACE: 'ns',
  STRING_GROUP: 'sg',
  RANGE_TYPE: 'rangeType',
  RANGE_AMOUNT: 'rangeAmount',
  RANGE_UNIT: 'rangeUnit',
  RANGE_START: 'rangeStart',
  RANGE_END: 'rangeEnd',
} as const;

/** Relative-range units the app produces and `getDateBounds` can compute. */
const RANGE_UNITS = new Set<string>(['day', 'week', 'month', 'year']);

function isTimeUnit(
  value: string | null,
): value is DateRangePickerProps.TimeUnit {
  return value != null && RANGE_UNITS.has(value);
}

/** Reads a date range back; null for absent, malformed, or out-of-policy ranges
 *  (start<=end, no future, <=1 year). Uses the copy-free `dateRangeValidationCode`, so this codec
 *  needs no `TranslateFn`. */
function parseDateRange(
  params: URLSearchParams,
): DateRangePickerProps.Value | null {
  const type = params.get(PARAM.RANGE_TYPE);

  if (type === 'relative') {
    const amount = Number(params.get(PARAM.RANGE_AMOUNT));
    const unit = params.get(PARAM.RANGE_UNIT);
    if (Number.isInteger(amount) && amount > 0 && isTimeUnit(unit)) {
      const candidate = { type: 'relative', amount, unit } as const;
      return dateRangeValidationCode(candidate) === null ? candidate : null;
    }
    return null;
  }

  if (type === 'absolute') {
    const startDate = params.get(PARAM.RANGE_START);
    const endDate = params.get(PARAM.RANGE_END);
    // Both bounds must parse as dates so downstream never sees NaN.
    if (
      startDate &&
      endDate &&
      !Number.isNaN(Date.parse(startDate)) &&
      !Number.isNaN(Date.parse(endDate))
    ) {
      const candidate = { type: 'absolute', startDate, endDate } as const;
      return dateRangeValidationCode(candidate) === null ? candidate : null;
    }
    return null;
  }

  return null;
}

/** Encodes Track filters into a shareable query string (range written explicitly). */
export function trackStateToParams(state: TrackUrlState): URLSearchParams {
  const params = new URLSearchParams();

  if (state.namespace) params.set(PARAM.NAMESPACE, state.namespace);
  if (state.stringGroup) params.set(PARAM.STRING_GROUP, state.stringGroup);

  const range = state.dateRange;
  // Guard on isTimeUnit so encode/decode share one unit set — a unit the decoder
  // can't parse is not written, rather than encoded and then silently dropped.
  if (range?.type === 'relative' && isTimeUnit(range.unit)) {
    params.set(PARAM.RANGE_TYPE, 'relative');
    params.set(PARAM.RANGE_AMOUNT, String(range.amount));
    params.set(PARAM.RANGE_UNIT, range.unit);
  } else if (range?.type === 'absolute') {
    params.set(PARAM.RANGE_TYPE, 'absolute');
    params.set(PARAM.RANGE_START, range.startDate);
    params.set(PARAM.RANGE_END, range.endDate);
  }

  return params;
}

/** Decodes a query string into a validated `TrackUrlState`. */
export function paramsToTrackState(params: URLSearchParams): TrackUrlState {
  return {
    namespace: params.get(PARAM.NAMESPACE) ?? '',
    stringGroup: params.get(PARAM.STRING_GROUP) ?? '',
    dateRange: parseDateRange(params),
  };
}

/**
 * The Track page's shareable-URL codec, for {@link useUrlSharedState}. A
 * module-level constant so the hook's write callbacks keep a stable identity.
 */
export const TRACK_URL_CODEC: UrlStateCodec<TrackUrlState> = {
  encode: trackStateToParams,
  decode: paramsToTrackState,
};
